import { separatorsByNumberFormat, type NumberFormat } from '@/shared/lib/user-setting';

function decimalDigitsOf(input: string): { digits: string; scale: number } | null {
  const match = input.trim().match(/^(-?)(\d*)(?:[.,](\d*))?$/);
  if (!match) return null;
  const [, sign, whole = '', fraction = ''] = match;
  if (whole.length === 0 && fraction.length === 0) return null;
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, '');
  return { digits: `${sign}${digits}`, scale: fraction.length };
}

const SPACE_GROUP_SEPARATORS = [' ', '\u00A0', '\u202F'];

function escapedForPattern(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function typedDigitsOf(
  input: string,
  numberFormat: NumberFormat,
): { digits: string; scale: number } | null {
  const { group, decimal } = separatorsByNumberFormat[numberFormat];
  const groupSeparators = numberFormat === 'space-comma' ? SPACE_GROUP_SEPARATORS : [group];
  const groupPattern = `[${groupSeparators.map(escapedForPattern).join('')}]`;
  const pattern = new RegExp(
    `^(-?)([1-9]\\d{0,2}(?:${groupPattern}\\d{3})+|\\d*)(?:${escapedForPattern(decimal)}(\\d*))?$`,
  );
  const match = input.trim().match(pattern);
  if (!match) return null;
  const [, sign, groupedWhole = '', fraction = ''] = match;
  const whole = groupedWhole.replace(new RegExp(groupPattern, 'g'), '');
  if (whole.length === 0 && fraction.length === 0) return null;
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, '');
  return { digits: `${sign}${digits}`, scale: fraction.length };
}

/**
 * A number the reader typed, read as an exact amount/scale pair in their number format.
 *
 * Only the format's own decimal mark is a decimal mark. Its group separator is optional,
 * but where it appears it must group the whole part in threes (`1,234,567` under
 * `comma-period`), with no leading zero in the first group, so `0,500` is never five
 * hundred; `space-comma` takes a plain space, a no-break space or a narrow
 * no-break space as its group separator. Anything else is `null`, the other format's
 * decimal mark included: `1,5` under `comma-period` could be one and a half or fifteen,
 * and guessing either sends a figure the reader did not mean. Every string
 * `formatAmountScale` prints reads back to the same pair.
 */
export function parseDecimalToAmountScale(
  input: string,
  numberFormat: NumberFormat,
): { amount: number; scale: number } | null {
  const parsed = typedDigitsOf(input, numberFormat);
  if (parsed == null) return null;
  const amount = Number(parsed.digits);
  if (!Number.isSafeInteger(amount)) return null;
  return { amount, scale: parsed.scale };
}

/**
 * The backend's `percent_string` — `"42.13%"` — read as the exact pair `4213` at scale 2.
 *
 * A percent is a display string the client never recomputes, so this reads it rather than
 * deriving it: `parseFloat` would answer with a float for a figure the platform keeps as
 * an integer everywhere else, and comparing the string itself ranks `"9.5%"` above
 * `"42.13%"`. The pair is a `bigint` because a weight divides by a NAV and the backend
 * bounds neither: a near-zero NAV produces a percent with more digits than a JavaScript
 * number holds exactly, and that percent is still printed to the reader in full. A number
 * the reader can see must stay a number the client can rank, so the only `null` here is a
 * weight that is absent or is not a number at all.
 *
 * Shared by the positions sort and the allocation donut, which read the same field of the
 * same response and must not disagree about what it says.
 */
export function parsePercentToAmountScale(
  percent: string | null | undefined,
): { amount: bigint; scale: number } | null {
  if (percent === null || percent === undefined) return null;
  const parsed = decimalDigitsOf(percent.replace('%', ''));
  if (parsed == null) return null;
  return { amount: BigInt(parsed.digits), scale: parsed.scale };
}

function normalizedAmountScale(amount: number, scale: number): { amount: number; scale: number } {
  if (amount === 0) return { amount: 0, scale: 0 };
  let reducedAmount = amount;
  let reducedScale = scale;
  while (reducedScale > 0 && reducedAmount % 10 === 0) {
    reducedAmount /= 10;
    reducedScale -= 1;
  }
  return { amount: reducedAmount, scale: reducedScale };
}

function rescaledAmountScale(
  amount: number,
  scale: number,
  targetScale: number,
): { amount: number; scale: number } | null {
  if (scale === targetScale) return { amount, scale };
  if (scale < targetScale) {
    const scaled = amount * 10 ** (targetScale - scale);
    if (!Number.isSafeInteger(scaled)) return null;
    return { amount: scaled, scale: targetScale };
  }
  const divisor = 10 ** (scale - targetScale);
  const absolute = Math.abs(amount);
  const whole = Math.floor(absolute / divisor);
  const remainder = absolute - whole * divisor;
  const rounded = remainder * 2 >= divisor ? whole + 1 : whole;
  if (!Number.isSafeInteger(rounded)) return null;
  return { amount: amount < 0 ? -rounded : rounded, scale: targetScale };
}

/**
 * Multiply two amount/scale pairs and express the product at `targetScale`.
 *
 * Both factors are normalized to their own significant precision before the multiply.
 * Composing at `scaleA + scaleB` instead makes the product's magnitude depend on how many
 * trailing zeros the wire happened to carry: a scale-8 quantity of `4.00000000` at a
 * 2-decimal price overflows `Number.MAX_SAFE_INTEGER` and reports a trivially
 * representable value as unrepresentable. Normalizing first removes that dependency —
 * only the digits that carry information reach the multiply.
 *
 * The product is then rounded, half away from zero, to exactly `targetScale`, which the
 * caller sets to the precision the value is meaningful at (for a trade's gross, the
 * price's own scale). The composed scale is an artifact of the multiplication, so a
 * quantity's trailing precision must not widen the money figure; and `325.00` must not
 * narrow to `325`. `null` is reserved for a product that genuinely cannot be represented.
 */
export function multiplyAmountScale(
  amountA: number | null | undefined,
  scaleA: number | null | undefined,
  amountB: number | null | undefined,
  scaleB: number | null | undefined,
  targetScale: number | null | undefined,
): { amount: number; scale: number } | null {
  if (amountA === null || amountA === undefined || amountB === null || amountB === undefined) {
    return null;
  }
  if (scaleA === null || scaleA === undefined || scaleB === null || scaleB === undefined) {
    return null;
  }
  if (targetScale === null || targetScale === undefined) return null;
  if (!Number.isSafeInteger(amountA) || !Number.isSafeInteger(amountB)) return null;
  if (!Number.isInteger(scaleA) || !Number.isInteger(scaleB) || scaleA < 0 || scaleB < 0) {
    return null;
  }
  if (!Number.isInteger(targetScale) || targetScale < 0) return null;
  const normalizedA = normalizedAmountScale(amountA, scaleA);
  const normalizedB = normalizedAmountScale(amountB, scaleB);
  const amount = normalizedA.amount * normalizedB.amount;
  if (!Number.isSafeInteger(amount)) return null;
  return rescaledAmountScale(amount, normalizedA.scale + normalizedB.scale, targetScale);
}

/**
 * Whether `formatAmountScale` will render this pair as a number rather than a dash.
 *
 * Callers that decide *whether to show a row at all* must ask this, not `!= null`: a
 * present-but-unrepresentable amount passes a null check and then renders as `—`, which
 * puts a dash where the reader expects a figure and states nothing about why. The
 * predicate lives here, beside the formatter it describes, so the two cannot drift into
 * disagreeing about what is renderable.
 */
export function isRenderableAmountScale(
  amount: number | null | undefined,
  scale: number | null | undefined,
): boolean {
  if (amount === null || amount === undefined || scale === null || scale === undefined) {
    return false;
  }
  return Number.isSafeInteger(amount) && Number.isInteger(scale) && scale >= 0;
}

function renderedDigits(
  negative: boolean,
  absoluteDigits: string,
  scale: number,
  numberFormat: NumberFormat,
  trimZeros: boolean,
): string {
  const { group, decimal } = separatorsByNumberFormat[numberFormat];
  const padded = absoluteDigits.padStart(scale + 1, '0');
  const whole = padded.slice(0, padded.length - scale);
  let fraction = padded.slice(padded.length - scale);
  if (trimZeros) {
    fraction = fraction.replace(/0+$/, '');
  }
  const groupedWhole = whole.replace(/\B(?=(\d{3})+$)/g, group);
  const digits = fraction.length > 0 ? `${groupedWhole}${decimal}${fraction}` : groupedWhole;
  return negative ? `-${digits}` : digits;
}

/**
 * An amount/scale pair printed in the reader's number format, at exactly its own scale.
 *
 * Only the separators follow the format: the digits and the scale are the ones the
 * service sent, so a figure is never rounded or widened here. `trimZeros` drops trailing
 * fraction zeros, for a quantity whose ledger scale is wider than its meaning. An absent
 * or unrepresentable pair prints `—`.
 */
export function formatAmountScale(
  amount: number | null | undefined,
  scale: number | null | undefined,
  numberFormat: NumberFormat,
  options?: { trimZeros?: boolean },
): string {
  if (amount === null || amount === undefined || scale === null || scale === undefined) {
    return '—';
  }
  if (!isRenderableAmountScale(amount, scale)) {
    return '—';
  }
  return renderedDigits(
    amount < 0,
    Math.abs(amount).toString(),
    scale,
    numberFormat,
    options?.trimZeros ?? false,
  );
}

/**
 * The backend's `percent_string` printed in the reader's number format, digit for digit.
 *
 * The server writes every percent with a period (`"42.13%"`), whatever the reader's
 * format; this reads it through `parsePercentToAmountScale` and prints the same digits at
 * the same scale with the reader's separators (`"42,13%"` under `period-comma`). A weight
 * that is absent or is not a number prints `—`.
 */
export function formatPercent(
  percent: string | null | undefined,
  numberFormat: NumberFormat,
): string {
  const parsed = parsePercentToAmountScale(percent);
  if (parsed == null) return '—';
  const negative = parsed.amount < 0n;
  const absoluteDigits = (negative ? -parsed.amount : parsed.amount).toString();
  return `${renderedDigits(negative, absoluteDigits, parsed.scale, numberFormat, false)}%`;
}

export function formatCount(count: number, numberFormat: NumberFormat): string {
  return formatAmountScale(count, 0, numberFormat);
}
